import { useMemo, useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CustomEmail from '../pages/CustomEmail';
import databaseAPI from '../api/databaseAPI';
import { authFetch, authHeaders, resolveApiBaseUrl } from '../api/auth';
import '../styles/Settings.css';

const API_BASE_URL = resolveApiBaseUrl();

const Settings = () => {
  const [autoEmailHtmlView, setAutoEmailHtmlView] = useState('design'); // 'design' | 'source'
  const [activeSection, setActiveSection] = useState('auto-email'); // default open

  const [settings, setSettings] = useState({
    storeName: 'Your Store Name',
    notificationsEnabled: true,
    autoRefresh: false,
    refreshInterval: 5,

    // Auto email: Shipping Required
    autoShippingRequiredEmailEnabled: false,
    autoShippingRequiredEmailSubject: 'Your order is confirmed (shipping update)',
    autoShippingRequiredEmailHtml:
      '<h2>Hello</h2><p>Thanks for your order! We’re preparing your shipment now. You’ll receive another email once it ships.</p><p>Regards,<br/>Arts To Hearts Project</p>',
    autoShippingRequiredEmailText:
      'Hello\n\nThanks for your order! We’re preparing your shipment now. You’ll receive another email once it ships.\n\nRegards,\nArts To Hearts Project',
    autoShippingRequiredEmailReplyTo: 'support@artstoheartsproject.com, Orders@artstoheartsproject.com',
    autoShippingRequiredEmailDelayMs: 250,
  });

  const [saved, setSaved] = useState(false);
  const [invoiceOrderId, setInvoiceOrderId] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState('');
  const [invoiceDateTo, setInvoiceDateTo] = useState('');
  const [invoiceScope, setInvoiceScope] = useState('shipping'); // all | shipping | digital
  const [invoiceMessage, setInvoiceMessage] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  useEffect(() => {
    const savedSettings = localStorage.getItem('dashboard_settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // Strip any previously stored secrets from localStorage
        const {
          shopifyToken,
          awsAccessKeyId,
          awsSecretAccessKey,
          smtpPassword,
          emailjsUserId,
          ...safe
        } = parsed;
        setSettings((prev) => ({ ...prev, ...safe }));
        localStorage.setItem('dashboard_settings', JSON.stringify({
          ...safe,
          // never re-persist secrets
        }));
      } catch (_) {
        // ignore corrupt settings
      }
    }
  }, []);

  useEffect(() => {
    // Load backend auto-email config
    authFetch(`${API_BASE_URL}/auto-email/shipping-required/config/`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        if (!data?.success || !data?.config) return;
        const cfg = data.config;
        setSettings((prev) => ({
          ...prev,
          autoShippingRequiredEmailEnabled: !!cfg.enabled,
          autoShippingRequiredEmailSubject: cfg.subject ?? prev.autoShippingRequiredEmailSubject,
          autoShippingRequiredEmailHtml: cfg.html ?? prev.autoShippingRequiredEmailHtml,
          autoShippingRequiredEmailText: cfg.text ?? prev.autoShippingRequiredEmailText,
          autoShippingRequiredEmailReplyTo: cfg.reply_to ?? prev.autoShippingRequiredEmailReplyTo,
          autoShippingRequiredEmailDelayMs: cfg.delay_ms ?? prev.autoShippingRequiredEmailDelayMs,
        }));
      })
      .catch(() => {});
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }, { align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean'],
      ],
      clipboard: { matchVisual: false },
    }),
    [],
  );

  const quillFormats = useMemo(
    () => [
      'font',
      'size',
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'script',
      'list',
      'bullet',
      'indent',
      'direction',
      'align',
      'blockquote',
      'code-block',
      'link',
      'image',
    ],
    [],
  );

  const setAutoEmailHtml = (value) => {
    setSettings((prev) => ({
      ...prev,
      autoShippingRequiredEmailHtml: value,
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = () => {
    const {
      shopifyToken,
      shopifyShop,
      awsAccessKeyId,
      awsSecretAccessKey,
      awsRegion,
      fromEmail,
      senderName,
      smtpHost,
      customSmtpHost,
      smtpPort,
      smtpSecure,
      smtpUsername,
      smtpPassword,
      smtpFromEmail,
      smtpSenderName,
      emailjsServiceId,
      emailjsTemplateId,
      emailjsUserId,
      emailService,
      ...safeSettings
    } = settings;
    localStorage.setItem('dashboard_settings', JSON.stringify(safeSettings));

    // Save backend auto-email config
    authFetch(`${API_BASE_URL}/auto-email/shipping-required/config/`, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        enabled: settings.autoShippingRequiredEmailEnabled,
        subject: settings.autoShippingRequiredEmailSubject,
        html: settings.autoShippingRequiredEmailHtml,
        text: settings.autoShippingRequiredEmailText,
        reply_to: settings.autoShippingRequiredEmailReplyTo,
        delay_ms: settings.autoShippingRequiredEmailDelayMs,
      }),
    }).catch(() => {});

    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    localStorage.removeItem('dashboard_settings');
    setSettings({
      storeName: 'Your Store Name',
      notificationsEnabled: true,
      autoRefresh: false,
      refreshInterval: 5,

      autoShippingRequiredEmailEnabled: false,
      autoShippingRequiredEmailSubject: 'Your order is confirmed (shipping update)',
      autoShippingRequiredEmailHtml:
        '<h2>Hello</h2><p>Thanks for your order! We’re preparing your shipment now. You’ll receive another email once it ships.</p><p>Regards,<br/>Arts To Hearts Project</p>',
      autoShippingRequiredEmailText:
        'Hello\n\nThanks for your order! We’re preparing your shipment now. You’ll receive another email once it ships.\n\nRegards,\nArts To Hearts Project',
      autoShippingRequiredEmailReplyTo: 'support@artstoheartsproject.com, Orders@artstoheartsproject.com',
      autoShippingRequiredEmailDelayMs: 250,
    });
  };

  const triggerDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSingleInvoiceDownload = async () => {
    const orderId = String(invoiceOrderId || '').trim();
    if (!orderId) {
      setInvoiceMessage('Please enter a valid Order ID.');
      return;
    }
    setInvoiceLoading(true);
    setInvoiceMessage('');
    const res = await databaseAPI.downloadInvoice(orderId);
    if (!res.success) {
      setInvoiceMessage(res.error || 'Invoice download failed.');
      setInvoiceLoading(false);
      return;
    }
    triggerDownload(res.blob, `invoice-${orderId}.pdf`);
    setInvoiceLoading(false);
    setInvoiceMessage('Invoice downloaded successfully.');
  };

  const handleBulkInvoiceDownload = async () => {
    if (!invoiceDateFrom || !invoiceDateTo) {
      setInvoiceMessage('Please select both date range fields.');
      return;
    }
    if (invoiceDateFrom > invoiceDateTo) {
      setInvoiceMessage('From date must be less than or equal to To date.');
      return;
    }
    setInvoiceLoading(true);
    setInvoiceMessage('');
    const res = await databaseAPI.downloadBulkInvoices(invoiceDateFrom, invoiceDateTo, invoiceScope);
    if (!res.success) {
      setInvoiceMessage(res.error || 'Bulk invoice ZIP download failed.');
      setInvoiceLoading(false);
      return;
    }
    triggerDownload(res.blob, `invoices-${invoiceScope}-${invoiceDateFrom}-to-${invoiceDateTo}.zip`);
    setInvoiceLoading(false);
    setInvoiceMessage('Bulk invoice ZIP downloaded successfully.');
  };

  return (
    <div className="settings-shell">
      <div className="settings-topbar">
        <div className="settings-topbar-left">
          <div className="settings-kicker">Admin</div>
          <h1 className="settings-title">Settings</h1>
          <div className="settings-subtitle">Configure Shopify + dashboard automation</div>
        </div>

        <div className="settings-topbar-actions">
          <button onClick={handleSave} className="btn btn-primary">
            💾 Save
          </button>
          <button onClick={handleReset} className="btn btn-secondary">
            🔄 Reset
          </button>
        </div>
      </div>

      {saved ? <div className="settings-toast">✅ Saved</div> : null}

      <div className="settings-layout">
        <aside className="settings-nav">
          <div className="settings-nav-title">Sections</div>
          <button
            type="button"
            className={activeSection === 'auto-email' ? 'settings-nav-link settings-nav-link-active' : 'settings-nav-link'}
            onClick={() => setActiveSection('auto-email')}
          >
            📧 Auto Email
          </button>
          <button
            type="button"
            className={activeSection === 'custom-email' ? 'settings-nav-link settings-nav-link-active' : 'settings-nav-link'}
            onClick={() => setActiveSection('custom-email')}
          >
            ✉️ Custom Email
          </button>
          <button
            type="button"
            className={activeSection === 'dashboard' ? 'settings-nav-link settings-nav-link-active' : 'settings-nav-link'}
            onClick={() => setActiveSection('dashboard')}
          >
            🎛️ Dashboard
          </button>
          <button
            type="button"
            className={activeSection === 'invoice' ? 'settings-nav-link settings-nav-link-active' : 'settings-nav-link'}
            onClick={() => setActiveSection('invoice')}
          >
            🧾 Invoice
          </button>
        </aside>

        <main className="settings-main">
          {activeSection === 'auto-email' ? (
          <section className="settings-card">
            <div className="settings-card-head">
              <div>
                <div className="settings-card-title">Auto Email (Shipping Required)</div>
                <div className="settings-card-desc">
                  Send an informational email automatically after order if shipping is required.
                </div>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="auto-email-card">
                <div className="auto-email-top">
                  <div className="auto-email-top-text">
                    <div className="auto-email-top-title">Feature Toggle</div>
                    <div className="auto-email-top-subtitle">
                      ON = auto email will be sent for latest shipping-required orders. OFF = no email will be sent.
                    </div>
                  </div>

                  <label className="ace-switch">
                    <input
                      type="checkbox"
                      name="autoShippingRequiredEmailEnabled"
                      checked={settings.autoShippingRequiredEmailEnabled}
                      onChange={handleChange}
                    />
                    <span className="ace-slider" />
                    <span className="ace-switch-text">{settings.autoShippingRequiredEmailEnabled ? 'ON' : 'OFF'}</span>
                  </label>
                </div>

                <div className="auto-email-grid">
                  <div className="form-group">
                    <label htmlFor="autoShippingRequiredEmailSubject">Subject</label>
                    <input
                      type="text"
                      id="autoShippingRequiredEmailSubject"
                      name="autoShippingRequiredEmailSubject"
                      value={settings.autoShippingRequiredEmailSubject}
                      onChange={handleChange}
                      placeholder="Subject"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="autoShippingRequiredEmailDelayMs">Rate limit delay (ms)</label>
                    <input
                      type="number"
                      id="autoShippingRequiredEmailDelayMs"
                      name="autoShippingRequiredEmailDelayMs"
                      value={settings.autoShippingRequiredEmailDelayMs}
                      onChange={handleChange}
                      min="0"
                      max="60000"
                    />
                  </div>

                  <div className="form-group auto-email-full">
                    <label htmlFor="autoShippingRequiredEmailReplyTo">Reply-To</label>
                    <input
                      type="text"
                      id="autoShippingRequiredEmailReplyTo"
                      name="autoShippingRequiredEmailReplyTo"
                      value={settings.autoShippingRequiredEmailReplyTo}
                      onChange={handleChange}
                      placeholder="info@..."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>HTML Template</label>
                  <div className="ace-tabs">
                    <button
                      type="button"
                      className={autoEmailHtmlView === 'design' ? 'ace-tab ace-tab-active' : 'ace-tab'}
                      onClick={() => setAutoEmailHtmlView('design')}
                    >
                      Design
                    </button>
                    <button
                      type="button"
                      className={autoEmailHtmlView === 'source' ? 'ace-tab ace-tab-active' : 'ace-tab'}
                      onClick={() => setAutoEmailHtmlView('source')}
                    >
                      HTML source
                    </button>
                  </div>

                  {autoEmailHtmlView === 'source' ? (
                    <textarea
                      id="autoShippingRequiredEmailHtml"
                      name="autoShippingRequiredEmailHtml"
                      value={settings.autoShippingRequiredEmailHtml}
                      onChange={(e) => setAutoEmailHtml(e.target.value)}
                      rows="8"
                      className="ace-textarea ace-textarea-mono"
                    />
                  ) : (
                    <div className="ace-quill-shell">
                      <ReactQuill
                        theme="snow"
                        value={settings.autoShippingRequiredEmailHtml}
                        onChange={setAutoEmailHtml}
                        modules={quillModules}
                        formats={quillFormats}
                      />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="autoShippingRequiredEmailText">Text Template</label>
                  <textarea
                    id="autoShippingRequiredEmailText"
                    name="autoShippingRequiredEmailText"
                    value={settings.autoShippingRequiredEmailText}
                    onChange={handleChange}
                    rows="8"
                    className="ace-textarea"
                  />
                </div>


              </div>
            </div>
          </section>
          ) : null}

          {activeSection === 'custom-email' ? (
          <section className="settings-card">
            <div className="settings-card-head">
              <div>
                <div className="settings-card-title">Custom Email</div>
                <div className="settings-card-desc">Send a one-off/bulk email via CSV or manual emails.</div>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-custom-email-embed">
                <CustomEmail embedded />
              </div>
            </div>
          </section>
          ) : null}

          {activeSection === 'dashboard' ? (
          <section className="settings-card">
            <div className="settings-card-head">
              <div>
                <div className="settings-card-title">Dashboard</div>
                <div className="settings-card-desc">UI behavior and refresh settings.</div>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-grid-2">
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="notificationsEnabled"
                      checked={settings.notificationsEnabled}
                      onChange={handleChange}
                    />
                    Enable Email Notifications
                  </label>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="autoRefresh"
                      checked={settings.autoRefresh}
                      onChange={handleChange}
                    />
                    Auto Refresh Orders
                  </label>
                </div>

                {settings.autoRefresh ? (
                  <div className="form-group settings-span-2">
                    <label htmlFor="refreshInterval">Refresh Interval (minutes)</label>
                    <input
                      type="number"
                      id="refreshInterval"
                      name="refreshInterval"
                      value={settings.refreshInterval}
                      onChange={handleChange}
                      min="1"
                      max="60"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </section>
          ) : null}

          {activeSection === 'invoice' ? (
          <section className="settings-card">
            <div className="settings-card-head">
              <div>
                <div className="settings-card-title">Invoice Download</div>
                <div className="settings-card-desc">Download single invoice PDF or bulk ZIP by date range.</div>
              </div>
            </div>
            <div className="settings-card-body">
              <div className="settings-grid-2 invoice-grid">
                <div className="form-group">
                  <label htmlFor="invoiceOrderId">Order ID</label>
                  <input
                    id="invoiceOrderId"
                    type="text"
                    value={invoiceOrderId}
                    onChange={(e) => setInvoiceOrderId(e.target.value)}
                    placeholder="e.g. 4039 or 6575453577326"
                  />
                  <button
                    type="button"
                    className="btn btn-primary invoice-action-btn"
                    onClick={handleSingleInvoiceDownload}
                    disabled={invoiceLoading}
                  >
                    {invoiceLoading ? 'Downloading...' : 'Download Invoice PDF'}
                  </button>
                </div>

                <div className="form-group">
                  <label>Order Scope</label>
                  <div className="invoice-scope-switcher">
                    <button
                      type="button"
                      className={`invoice-scope-pill ${invoiceScope === 'all' ? 'active' : ''}`}
                      onClick={() => setInvoiceScope('all')}
                    >
                      <span className="invoice-scope-dot all" />
                      All
                    </button>
                    <button
                      type="button"
                      className={`invoice-scope-pill ${invoiceScope === 'shipping' ? 'active' : ''}`}
                      onClick={() => setInvoiceScope('shipping')}
                    >
                      <span className="invoice-scope-dot shipping" />
                      Shipping Required
                    </button>
                    <button
                      type="button"
                      className={`invoice-scope-pill ${invoiceScope === 'digital' ? 'active' : ''}`}
                      onClick={() => setInvoiceScope('digital')}
                    >
                      <span className="invoice-scope-dot digital" />
                      Non Shipping
                    </button>
                  </div>

                  <label htmlFor="invoiceDateFrom">From Date</label>
                  <input
                    id="invoiceDateFrom"
                    type="date"
                    value={invoiceDateFrom}
                    onChange={(e) => setInvoiceDateFrom(e.target.value)}
                  />

                  <label htmlFor="invoiceDateTo" className="invoice-date-label">To Date</label>
                  <input
                    id="invoiceDateTo"
                    type="date"
                    value={invoiceDateTo}
                    onChange={(e) => setInvoiceDateTo(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary invoice-action-btn"
                    onClick={handleBulkInvoiceDownload}
                    disabled={invoiceLoading}
                  >
                    {invoiceLoading ? 'Preparing ZIP...' : 'Download Bulk Invoice ZIP'}
                  </button>
                </div>
              </div>

              {invoiceMessage ? (
                <div className="info-box invoice-info-box">
                  <h4>Invoice Status</h4>
                  <p>{invoiceMessage}</p>
                </div>
              ) : null}
            </div>
          </section>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default Settings; 